import { call, put, takeEvery, takeLatest } from "redux-saga/effects";

import * as apis from "../../../network/apis/companyPortalLogisticsIssues";
import { showErrorMsg, showSuccessMsg } from "../../../utils/Helpers";
import {
  normalizeExportWorkbookBlob,
  parseContentDispositionFilename,
  readBlobErrorMessage,
  triggerBlobDownload,
} from "../../../utils/reportExportDownload";
import * as actions from "./actions";
import * as types from "./types";

const extractListItems = (response) => response?.data?.data ?? [];

const getListMeta = (response) => ({
  currentPage: Number(response?.headers?.["current-page"] || 1),
  count: Number(response?.headers?.["total-count"] || 0),
  pages: Number(response?.headers?.["total-pages"] || 1),
});

function* getCompanyLogisticsIssuesSaga({ payload }) {
  try {
    const response = yield call(apis.getCompanyLogisticsIssues, payload);
    yield put(
      actions.getCompanyLogisticsIssuesResponse({
        list: extractListItems(response),
        meta: getListMeta(response),
      }),
    );
  } catch (error) {
    yield call(showErrorMsg, error.response?.data?.message);
    yield put(
      actions.getCompanyLogisticsIssuesResponse({
        list: [],
        meta: { currentPage: 1, count: 0, pages: 1 },
      }),
    );
  }
}

function* createCompanyLogisticsIssueSaga({ payload }) {
  const { data, onSuccess, onError } = payload || {};
  try {
    const response = yield call(apis.createCompanyLogisticsIssue, data);
    yield put(
      actions.createCompanyLogisticsIssueResponse(
        response?.data?.data ?? null,
      ),
    );
    const message = response?.data?.message;
    if (message) yield call(showSuccessMsg, message, true);
    if (typeof onSuccess === "function") onSuccess(response);
  } catch (error) {
    yield put(actions.createCompanyLogisticsIssueResponse(null));
    const message = error?.response?.data?.message;
    if (message) yield call(showErrorMsg, message);
    if (typeof onError === "function") onError(error);
  }
}

function* getCompanyLogisticsIssueDetailSaga({ payload }) {
  const id = payload?.id ?? payload;
  try {
    const response = yield call(apis.getCompanyLogisticsIssue, id);
    yield put(
      actions.getCompanyLogisticsIssueDetailResponse(
        response?.data?.data ?? null,
      ),
    );
  } catch (error) {
    yield call(showErrorMsg, error.response?.data?.message);
    yield put(actions.getCompanyLogisticsIssueDetailResponse(null));
  }
}

function* updateCompanyLogisticsIssueSaga({ payload }) {
  const { id, data, onSuccess, onError } = payload || {};
  try {
    const response = yield call(apis.updateCompanyLogisticsIssue, { id, data });
    const next = response?.data?.data ?? null;
    yield put(actions.updateCompanyLogisticsIssueResponse(next));
    const message = response?.data?.message;
    if (message) yield call(showSuccessMsg, message, true);
    if (typeof onSuccess === "function") onSuccess(response);
  } catch (error) {
    yield put(actions.updateCompanyLogisticsIssueResponse(null));
    const message = error?.response?.data?.message;
    if (message) yield call(showErrorMsg, message);
    if (typeof onError === "function") onError(error);
  }
}

function* getCompanyLogisticsIssueCommentsSaga({ payload }) {
  const id = payload?.id ?? payload;
  const params = payload?.params ?? { page: -1 };
  try {
    const response = yield call(apis.getCompanyLogisticsIssueComments, id, params);
    yield put(
      actions.getCompanyLogisticsIssueCommentsResponse(
        extractListItems(response),
      ),
    );
  } catch (error) {
    yield call(showErrorMsg, error.response?.data?.message);
    yield put(actions.getCompanyLogisticsIssueCommentsResponse([]));
  }
}

function* createCompanyLogisticsIssueCommentSaga({ payload }) {
  const { id, data, onSuccess, onError } = payload || {};
  try {
    const response = yield call(apis.createCompanyLogisticsIssueComment, {
      id,
      data,
    });
    yield put(
      actions.createCompanyLogisticsIssueCommentResponse(
        response?.data?.data ?? null,
      ),
    );
    const message = response?.data?.message;
    if (message) yield call(showSuccessMsg, message, true);
    if (typeof onSuccess === "function") onSuccess(response);
  } catch (error) {
    yield put(actions.createCompanyLogisticsIssueCommentResponse(null));
    const message = error?.response?.data?.message;
    if (message) yield call(showErrorMsg, message);
    if (typeof onError === "function") onError(error);
  }
}

function* exportCompanyLogisticsIssuesSaga({ payload }) {
  const params = payload?.params || payload || {};
  try {
    const response = yield call(apis.exportCompanyLogisticsIssues, params);
    const blob = response.data;

    const contentType = String(response.headers?.["content-type"] || "");
    if (
      contentType.includes("application/json") ||
      (blob instanceof Blob && blob.type.includes("application/json"))
    ) {
      const text = yield call([blob, blob.text]);
      const parsed = JSON.parse(text);
      throw Object.assign(new Error(parsed?.message || "Export failed"), {
        response: { data: parsed },
      });
    }

    const rawBlob =
      blob instanceof Blob
        ? blob
        : new Blob([blob], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
    const fileBlob = yield call(normalizeExportWorkbookBlob, rawBlob);
    const filename =
      parseContentDispositionFilename(
        response.headers?.["content-disposition"],
      ) ||
      (params.event_id
        ? `logistics_issues_${params.event_id}.xlsx`
        : "logistics_issues.xlsx");
    yield call(triggerBlobDownload, fileBlob, filename);
    yield call(showSuccessMsg, "File downloaded successfully!", true);
  } catch (error) {
    let message = error?.response?.data?.message || error?.message;
    if (!message || message === "Export failed") {
      const blobMessage = yield call(
        readBlobErrorMessage,
        error?.response?.data,
      );
      if (blobMessage) message = blobMessage;
    }
    if (message) yield call(showErrorMsg, message);
  }
}

export default function* companyLogisticsIssuesSagas() {
  yield takeLatest(
    types.GET_COMPANY_LOGISTICS_ISSUES_REQUEST,
    getCompanyLogisticsIssuesSaga,
  );
  yield takeEvery(
    types.CREATE_COMPANY_LOGISTICS_ISSUE_REQUEST,
    createCompanyLogisticsIssueSaga,
  );
  yield takeLatest(
    types.GET_COMPANY_LOGISTICS_ISSUE_DETAIL_REQUEST,
    getCompanyLogisticsIssueDetailSaga,
  );
  yield takeEvery(
    types.UPDATE_COMPANY_LOGISTICS_ISSUE_REQUEST,
    updateCompanyLogisticsIssueSaga,
  );
  yield takeLatest(
    types.GET_COMPANY_LOGISTICS_ISSUE_COMMENTS_REQUEST,
    getCompanyLogisticsIssueCommentsSaga,
  );
  yield takeEvery(
    types.CREATE_COMPANY_LOGISTICS_ISSUE_COMMENT_REQUEST,
    createCompanyLogisticsIssueCommentSaga,
  );
  yield takeEvery(
    types.EXPORT_COMPANY_LOGISTICS_ISSUES_REQUEST,
    exportCompanyLogisticsIssuesSaga,
  );
}
